import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Beer, Clock, MapPin, Bell, BellRing, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const HappyHourAlerts = () => {
  const navigate = useNavigate();
  const [activeHours, setActiveHours] = useState([]);
  const [upcomingHours, setUpcomingHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState({
    happy_hour_alerts: true,
    alert_minutes_before: 30
  });
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  useEffect(() => {
    fetchHappyHours();
    checkNotificationPermission();
  }, []);

  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationsEnabled(permission === 'granted');
      if (permission === 'granted') {
        toast.success('Push notifications enabled!');
        // Send test notification
        new Notification('🍻 Happy Hour Alerts Active!', {
          body: "You'll be notified when happy hour starts at nearby bars",
          icon: '/barbuzz-logo.jpeg'
        });
      }
    }
  };

  const fetchHappyHours = async () => {
    try {
      const response = await axios.get(`${API}/happy-hour/active`);
      setActiveHours(response.data.active || []);
      setUpcomingHours(response.data.upcoming || []);
    } catch (error) {
      console.log('Happy hour data not available');
      // Demo data
      setActiveHours([
        {
          bar: { id: '1', name: 'The Blue Lounge', address: '123 Main St', image_url: null },
          schedule: { description: '$5 Draft Beers & $7 Cocktails', discount_percent: 25 },
          status: 'active',
          distance_miles: 0.3,
          ends_at: '19:00'
        }
      ]);
      setUpcomingHours([
        {
          bar: { id: '2', name: 'Neon Nights', address: '456 Bar Ave', image_url: null },
          schedule: { description: 'Half Price Apps & $6 Wine', discount_percent: 30 },
          status: 'upcoming',
          distance_miles: 0.5,
          starts_at: '17:00'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      await axios.post(`${API}/happy-hour/subscribe`, preferences);
      toast.success('Subscribed to Happy Hour alerts!');
    } catch (error) {
      console.error('Failed to subscribe:', error);
    }
  };

  if (loading) {
    return (
      <Card className="glass p-4 border border-amber-500/30">
        <div className="flex items-center gap-2 text-amber-400">
          <Beer className="w-5 h-5 animate-pulse" />
          <span>Loading happy hours...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className="glass p-4 border border-amber-500/30">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
            <Beer className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Happy Hour Alerts</h3>
            <p className="text-xs text-slate-400">Never miss a deal</p>
          </div>
        </div>
        <Button
          size="sm"
          variant={notificationsEnabled ? 'default' : 'outline'}
          onClick={requestNotificationPermission}
          className={notificationsEnabled 
            ? 'bg-amber-500 hover:bg-amber-600 text-black' 
            : 'border-amber-500 text-amber-400 hover:bg-amber-500/20'
          }
        >
          {notificationsEnabled ? (
            <><BellRing className="w-4 h-4 mr-1" /> Enabled</>
          ) : (
            <><Bell className="w-4 h-4 mr-1" /> Enable</>
          )}
        </Button>
      </div>

      {/* Active Happy Hours */}
      {activeHours.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <span className="text-sm font-medium text-green-400">Happening Now</span>
          </div>
          <div className="space-y-2">
            {activeHours.map((hh, idx) => (
              <div
                key={idx}
                className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg cursor-pointer hover:bg-green-500/20 transition-all"
                onClick={() => navigate(`/bar/${hh.bar.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">{hh.bar.name}</p>
                    <p className="text-xs text-slate-400 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {hh.distance_miles} mi away
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-green-400">
                      {hh.schedule.discount_percent}% OFF
                    </p>
                    <p className="text-xs text-slate-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Ends {hh.ends_at}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-slate-300 mt-1">{hh.schedule.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Happy Hours */}
      {upcomingHours.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-medium text-amber-400">Coming Up</span>
          </div>
          <div className="space-y-2">
            {upcomingHours.map((hh, idx) => (
              <div
                key={idx}
                className="p-3 bg-slate-800/50 border border-slate-700 rounded-lg cursor-pointer hover:border-amber-500/50 transition-all"
                onClick={() => navigate(`/bar/${hh.bar.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">{hh.bar.name}</p>
                    <p className="text-xs text-slate-400">{hh.schedule.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <p className="text-sm text-amber-400">Starts {hh.starts_at}</p>
                      <p className="text-xs text-slate-400">{hh.distance_miles} mi</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeHours.length === 0 && upcomingHours.length === 0 && (
        <div className="text-center py-4 text-slate-400">
          <Beer className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No happy hours nearby right now</p>
          <p className="text-xs mt-1">Enable notifications to get alerted!</p>
        </div>
      )}
    </Card>
  );
};

export default HappyHourAlerts;
